import { useEffect, useRef, useState } from "react";
import { Camera, X, ImagePlus } from "lucide-react";

/**
 * Photo attachment picker for CCP deviation submissions.
 * - Accepts image files (uses the camera on mobile via `capture="environment"`).
 * - Keeps the selected `File` objects in the parent's state so the parent can
 *   upload them AFTER the reading is created (we need the reading id first).
 * - Renders live thumbnail previews with a per-file remove button.
 *
 * Props:
 *   files: File[]              current selection
 *   onChange: (File[]) => void update the selection
 *   maxFiles?: number          default 5 — mirror what the backend can render neatly
 *   disabled?: boolean
 */
export default function DeviationPhotoPicker({ files = [], onChange, maxFiles = 5, disabled = false }) {
  const inputRef = useRef(null);
  const [previews, setPreviews] = useState([]);

  // Create/revoke object URLs whenever `files` changes so thumbnails don't leak.
  // Uses state (not a ref) so the render actually reflects the new URLs —
  // a ref mutation inside useEffect does not schedule a re-render.
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [files]);

  const addFiles = (list) => {
    const next = [...files];
    for (const f of list) {
      if (next.length >= maxFiles) break;
      if (!f.type?.startsWith("image/")) continue;
      next.push(f);
    }
    onChange(next);
  };

  const remove = (idx) => onChange(files.filter((_, i) => i !== idx));

  return (
    <div data-testid="dev-photo-picker">
      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
        Evidence photos (optional, up to {maxFiles})
      </label>
      <p className="text-xs opacity-60 mb-2">
        Take or upload photos of the thermometer, product, or corrective action taken. Attached to the deviation record and included in the audit PDF.
      </p>
      <div className="flex flex-wrap gap-2">
        {previews.map((url, i) => (
          <div key={i} className="relative" data-testid={`dev-photo-thumb-${i}`}
            style={{ width: 72, height: 72, borderRadius: 8, overflow: "hidden", border: "1px solid var(--vb-border)" }}>
            <img src={url} alt={`Evidence ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <button type="button" onClick={() => remove(i)} disabled={disabled}
              data-testid={`dev-photo-remove-${i}`}
              className="absolute top-0.5 right-0.5 grid place-items-center"
              style={{ width: 20, height: 20, borderRadius: 999, background: "rgba(0,0,0,0.6)", color: "#fff", border: 0, cursor: "pointer" }}
              title="Remove">
              <X size={12} />
            </button>
          </div>
        ))}
        {files.length < maxFiles && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={disabled}
            data-testid="dev-photo-add-btn"
            className="grid place-items-center vb-hover-lift"
            style={{ width: 72, height: 72, borderRadius: 8, border: "1px dashed var(--vb-border)", background: "var(--vb-bg)", cursor: "pointer", color: "var(--vb-text-2)" }}
            title="Add photo">
            {files.length === 0 ? <Camera size={22} /> : <ImagePlus size={22} />}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          addFiles(Array.from(e.target.files || []));
          // Reset input so re-selecting the same file still fires onChange.
          e.target.value = "";
        }}
        data-testid="dev-photo-input"
      />
    </div>
  );
}
